import type {UIMessagePart,} from 'ai';
import {type ClassValue, clsx} from 'clsx';
import {formatISO} from 'date-fns';
import {twMerge} from 'tailwind-merge';
import type {DBMessage} from '@/lib/db/schema';
import {ChatMessage, ChatTools, CustomUIDataTypes, UserRole} from './types';
import {basePath as BASE_PATH} from "./config";
import {v4 as uuidv4} from 'uuid';
import {getRapidsVerticalName, getEnvironment} from './constants';

/**
 * returns true if the site is running on the dev's local machine
 */
export const isLocalhost = () => process.env.NODE_ENV === 'development';

/**
 * returns true if the site is a released development/nonprod version of the application
 */
export const isNonProdRelease = () => {
  return (process.env.ENVIRONMENT === 'nonprod' || process.env.ENVIRONMENT === 'sandbox')
    && process.env.NODE_ENV === 'production';
};

/**
 * returns true if the site is a PR build running in a non-prod environment
 */
export const isPrBuild = () => {
  return isNonProdRelease() && getRapidsVerticalName().includes('rpds-');
}

/**
 * returns true if the site is running in staging environment
 */
export const isStaging = () => {
  return process.env.ENVIRONMENT === 'staging';
};

/**
 * returns true if the site is running in production environment
 */
export const isProduction = () => {
  return process.env.ENVIRONMENT === 'production';
};

/**
 * returns true if the app is running client side, false if it is running on the server
 */
export const isClientSide = (): boolean => typeof window !== 'undefined'

/**
 * returns true if the site is running on the dev's local machine
 */
export const isDevelopment = () => process.env.NODE_ENV === 'development';

/**
 * Combines class names using clsx and merges Tailwind CSS classes with twMerge
 */
export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs));
}

/**
 * Generates a UUID v4 string
 */
export function generateUUID(): string {
  return uuidv4();
}

/**
 * Sanitizes text by removing <has_function_call> tags and normalizing bullet points.
 * Converts bullet characters (•●) to markdown list syntax (- ) while preserving
 * bullets inside markdown table rows (lines starting with |).
 */
export function sanitizeText(text: string) {
  return text
    .replace('<has_function_call>', '')
    .replace(/^[•●]\s*/gm, '- ')
    .split('\n')
    .map(line => line.trimStart().startsWith('|')
      ? line
      : line.replace(/(?<=\S)[ ]*[•●]\s*/g, '\n- '))
    .join('\n');
}

/**
 * Converts database message format to UI message format
 */
export function convertToUIMessages(messages: DBMessage[]): ChatMessage[] {
  return messages.map((message) => ({
    id: message.id,
    role: message.role as 'user' | 'assistant' | 'system',
    parts: message.parts as UIMessagePart<CustomUIDataTypes, ChatTools>[],
    metadata: {
      createdAt: formatISO(message.createdAt),
      isDeidentified: message.isDeidentified,
      status: message.status as "pending" | "streaming" | "complete" | "error" | undefined,
    },
    ...(message.suggestedFollowUps && { suggestedFollowUps: message.suggestedFollowUps }),
  }));
}

/**
 * Extracts and concatenates text parts from a ChatMessage
 */
export function getTextFromMessage(message: ChatMessage): string {
  return message.parts
    .filter((part) => part.type === 'text')
    .map((part) => part.text)
    .join('');
}

/**
 * Determines if a message is incomplete and should be resumed.
 * A message is incomplete if it's not cancelled and either:
 * - It's a user message (AI response hasn't started)
 * - It has no status, or status is "pending" or "streaming"
 */
export function isMessageIncomplete(message: ChatMessage): boolean {
  const status = message.metadata?.status;
  return status !== "cancelled" && status !== "cancel_requested" && (
    message.role === "user"
    || !status || status === "pending" || status === "streaming"
  );
}

/**
 * Utility functions for handling basePath-aware URLs
 */

/**
 * Creates a basePath-aware path for client-side navigation
 * @param path - The path to append to basePath (should start with /)
 * @returns Path with basePath prefix
 */
export function createBasepathPath(path: string): string {
  if (!path.startsWith('/')) {
    path = "/" + path;
  }

  return `${BASE_PATH}${path}`;
}

/**
 * Creates a full URL to a basePath-aware path
 * @param path
 */
export function createBasepathUrl(path: string): string {
  const origin = window.location.origin;
  return `${origin}${createBasepathPath(path)}`;
}

/**
 * Updates the browser URL using basePath-aware paths
 * @param path - The path to navigate to (should start with /)
 */
export function updateUrl(path: string): void {
  window.history.replaceState({}, "", createBasepathPath(path));
}

export const noOp = () => {
};

/**
 * Gets the root domain without subdomain from the current hostname
 * @returns Root domain URL (e.g., "https://rapids-health.com")
 */
export function getRootDomain(): string {
  const hostname = window.location.hostname;
  const parts = hostname.split(".");
  const rootDomain = parts.length >= 2 ? parts.slice(-2).join(".") : hostname;
  return `https://${rootDomain}`;
}

export function base64Encode(value: string): string {
  // Node JS environment will use Buffer to encode
  if (typeof Buffer !== "undefined") {
    return Buffer.from(value, "utf-8").toString("base64");
  }

  // in the browser, use TextEncoder and btoa
  const bytes = new TextEncoder().encode(value);
  let binary = "";

  for (let i = 0; i < bytes.length; i++) {
    binary += String.fromCharCode(bytes[i]);
  }

  return btoa(binary);
}
const ALPHABET = "ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz";

export function alphaEncode(value: string, length = 32): string {
  // encode a string into a standard set of alpha chars of a given length
  // e.g. "this is a test string" -> "MMPHLRqmbjLbOamGCatJohkFwlMtQVEp"
  let out = "";
  let acc = 0;

  for (let i = 0; i < value.length; i += 1) {
    acc = (acc + value.charCodeAt(i) * (i + 1)) % ALPHABET.length;
    out += ALPHABET[acc];
  }

  // Stretch if input is short
  while (out.length < length) {
    acc = (acc * 7 + 13) % ALPHABET.length;
    out += ALPHABET[acc];
  }

  return out.slice(0, length);
}

/**
 * Creates a reCAPTCHA action ID by alpha-encoding the vertical name and appending the action
 * @param verticalName - The RAPIDS vertical name (e.g., "rpds-1234")
 * @param action - The action being performed (e.g., "submit_support_ticket")
 * @returns The formatted reCAPTCHA action ID (e.g., "MMPHLRqmbjLbOamG_submit_support_ticket")
 */
export function createRecaptchaActionId(verticalName: string, action: string): string {
  return `${alphaEncode(verticalName)}_${action}`;
}

/**
 * returns True is given str is an email, otherwise False
 * @param str
 */
export function isEmail(str: string): boolean {
  const singleEmailRegex = /^[a-zA-Z0-9._%+\-]+@[a-zA-Z0-9.\-]+\.[a-zA-Z]{2,}$/;
  return singleEmailRegex.test(str);
}

export function censorEmail(email: string): string {
  if (!email || !email.includes("@")) {
    return "***";
  }

  const parts = email.split("@");

  if (parts.length !== 2) {
    return "***";
  }

  const local = parts[0];
  const domain = parts[1];

  const maskedLocal =
    local.length <= 2
      ? "*"
      : local[0] + "*".repeat(local.length - 2) + local[local.length - 1];

  const maskedDomain =
    domain.length <= 2
      ? "*"
      : domain[0] + "*".repeat(domain.length - 2) + domain[domain.length - 1];

  return maskedLocal + "@" + maskedDomain;
}

/**
 * Censors all email addresses found in a text string to prevent PII leakage.
 * Uses a global regex to find all emails and replaces them with censored versions.
 *
 * @param text - The text that may contain email addresses
 * @returns The text with all email addresses censored
 *
 * @example
 * censorEmailsInText("Contact john@example.com or jane@test.org")
 * // Returns: "Contact j**n@e*********m or j**e@t****g"
 */
export function censorEmailsInText(text: string): string {
  if (!text) {
    return text;
  }

  // Global version of EMAIL_REGEX to match all emails in the text
  const globalEmailRegex = /[a-zA-Z0-9._%+\-]+@[a-zA-Z0-9.\-]+\.[a-zA-Z]{2,}/g;

  return text.replace(globalEmailRegex, (match) => censorEmail(match));
}